import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, Loading, ErrorBox, StatusBadge } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const monday = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const MODE = { onsite: ["Sur site", "On site"], video: ["Visio", "Video"], phone: ["Téléphone", "Phone"] };

function Card({ it }) {
  const { tx, lbl } = useI18n();
  return (
    <Link to={`/interviews/${it.id}`} data-testid={`planning-item-${it.id}`} className="block rounded-md border border-slate-200 bg-white p-2.5 text-xs transition-colors hover:border-[#2176AE]">
      <div className="flex items-center justify-between gap-2"><b className="text-[#153A63] text-sm">{it.scheduled_at.slice(11, 16) || "—"}</b><StatusBadge status={it.status} /></div>
      <div className="mt-1 font-medium text-slate-800">{it.employee_label}</div>
      <div className="text-slate-500">{it.client} · {it.unit}</div>
      <div className="mt-1 flex flex-wrap gap-x-3 text-slate-600">
        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{it.duration_min || 60} min</span>
        <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{it.location || lbl(MODE, it.mode) || tx("Lieu à préciser", "Location TBC")}</span>
      </div>
      {it.role === "co" && <div className="mt-1 text-[10px] text-amber-700">{tx("Co-animateur", "Co-interviewer")}</div>}
    </Link>
  );
}

export default function Planning() {
  const { tx, lang } = useI18n();
  const { user } = useAuth();
  const [start, setStart] = useState(monday(new Date()));
  const [who, setWho] = useState("");
  const [auditors, setAuditors] = useState([]);
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/planning/auditors").then((r) => setAuditors(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setItems(null);
    api.get("/planning", { params: { start: iso(start), consultant_id: who || undefined } }).then((r) => setItems(r.data)).catch((e) => setErr(errMsg(e)));
  }, [start, who]);
  const days = [...Array(7)].map((_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; });
  const move = (n) => { const d = new Date(start); d.setDate(d.getDate() + 7 * n); setStart(d); };
  const fmt = (d) => d.toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR", { weekday: "short", day: "numeric", month: "short" });
  const total = (items || []).reduce((s, i) => s + (i.duration_min || 60), 0);
  return (
    <div>
      <PageHeader eyebrow={tx("Organisation", "Organisation")} title={tx("Planning des entretiens", "Interview planning")}
        subtitle={tx("Entretiens programmés de la semaine, avec lieu et durée.", "Interviews scheduled this week, with location and duration.")} />
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Button variant="outline" size="sm" data-testid="planning-prev" onClick={() => move(-1)}><ChevronLeft className="h-4 w-4" /></Button>
        <span className="font-display font-semibold text-[#153A63] min-w-[190px] text-center" data-testid="planning-week">{fmt(days[0])} – {fmt(days[6])}</span>
        <Button variant="outline" size="sm" data-testid="planning-next" onClick={() => move(1)}><ChevronRight className="h-4 w-4" /></Button>
        <Button variant="ghost" size="sm" data-testid="planning-today" onClick={() => setStart(monday(new Date()))}>{tx("Cette semaine", "This week")}</Button>
        {auditors.length > 1 && (
          <select data-testid="planning-auditor" className="nselect ml-auto" value={who} onChange={(e) => setWho(e.target.value)}>
            <option value="">{tx("Mon planning", "My planning")} ({user.name})</option>
            {auditors.filter((a) => a.id !== user.id).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        )}
      </div>
      {err && <ErrorBox error={err} />}
      {!items ? <Loading /> : (
        <>
          <div className="text-sm text-slate-600 mb-3" data-testid="planning-summary">{items.length} {tx("entretien(s)", "interview(s)")} · {Math.floor(total / 60)} h {String(total % 60).padStart(2, "0")}</div>
          <div className="grid gap-3 md:grid-cols-7" data-testid="planning-grid">
            {days.map((d) => {
              const list = items.filter((i) => i.scheduled_at.slice(0, 10) === iso(d));
              const today = iso(d) === iso(new Date());
              return (
                <div key={iso(d)} className={`rounded-lg border p-2 min-h-[120px] ${today ? "border-[#2176AE] bg-[#EAF3FA]" : "border-slate-200 bg-slate-50"}`} data-testid={`planning-day-${iso(d)}`}>
                  <div className={`text-xs font-semibold uppercase tracking-wide mb-2 ${today ? "text-[#2176AE]" : "text-slate-500"}`}>{fmt(d)}</div>
                  <div className="space-y-2">{list.length ? list.map((it) => <Card key={it.id} it={it} />) : <div className="text-[11px] text-slate-400">—</div>}</div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-500 mt-3">{tx("La date, le lieu et la durée se règlent depuis la fiche d'entretien (bouton Programmer).", "Date, location and duration are set from the interview sheet (Schedule button).")}</p>
        </>
      )}
    </div>
  );
}
